<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import DecoratedHeadline from "../components/DecoratedHeadline.svelte";
	import ReferenceFilter from "../components/References/ReferenceFilter.svelte";
	import Reference from "../components/References/Reference.svelte";
	import Button from "../components/Button.svelte";
	import HelpSection from "../components/HelpSection.svelte";
	import Contact from "../components/Contact.svelte";
	import type { ReferencesProps } from "./ReferencesProps.js";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import { CaseStudyCategory } from "../../../core/schema/CaseStudyCategory.js";
	import { tick } from "svelte";
	import { isEnum } from "../../../core/utils/enum/isEnum.js";
	import ShelfSystemFilter from "../components/ShelfSystemFilter.svelte";

	type $$Props = ReferencesProps;

	const { references, selectedCategory, filterOptions, selectedFilterOption, countOfAllShelfSystemReferences } =
		$$restProps as $$Props;

	const isCaseStudyCategory = isEnum(CaseStudyCategory);
	let selected = isCaseStudyCategory(selectedCategory) ? selectedCategory : undefined;

	let visibleReferences = 6;

	async function handleLoadMore(): Promise<void> {
		const referencesContainer = document.getElementById("reference") as HTMLDivElement;

		const initialScrollHeight = referencesContainer.scrollHeight;
		const previousLastReference = referencesContainer.lastElementChild;

		visibleReferences += 2;

		await tick();
		const firstNewReference = previousLastReference?.nextElementSibling ?? referencesContainer.lastElementChild;

		if (firstNewReference) {
			const offset = 100;

			const newReferencePosition = firstNewReference.getBoundingClientRect().top + window.scrollY - offset;
			const finalScrollHeight = referencesContainer.scrollHeight;
			const heightDifference = finalScrollHeight - initialScrollHeight;

			const adjustedPosition =
				heightDifference > 0 ? Math.min(newReferencePosition, window.scrollY + heightDifference) : newReferencePosition;

			window.scrollTo({
				top: adjustedPosition,
				behavior: "smooth",
			});
		}
	}

	const categories = Object.keys(CaseStudyCategory) as (keyof typeof CaseStudyCategory)[];
</script>

<Layout>
	<div class="mx-auto mt-20 flex flex-col justify-center px-4">
		<DecoratedHeadline h1>Reference</DecoratedHeadline>
		<p
			class="mx-auto my-6 max-w-[70.625rem] text-center text-lg font-bold leading-[1.25] text-gray-400 sm:text-[2rem] md:text-[3rem] lg:mb-8 lg:mt-16"
		>
			{#if selectedCategory !== undefined && !isCaseStudyCategory(selectedCategory)}
				{nbspify(
					`Prohlédněte si případové studie z kategorie ${selectedCategory.name}, kde vám představujeme naše řešení. Vždy dle potřeb a požadavků zákazníků.`,
				)}
			{:else}
				{nbspify(
					"Prohlédněte si případové studie, kde vám představujeme naše řešení. Vždy dle potřeb a požadavků zákazníků.",
				)}
			{/if}
		</p>
		<p class="text-bito-black mx-auto max-w-[50.25rem] text-center text-xs md:text-base">
			{nbspify("Ke každému projektu přistupujeme profesionálně a snažíme se najít ideální řešení pro dané zadání.")}
			<br />
			{nbspify(
				"Žádný projekt není příliš malý nebo příliš velký. Pokud zde nenaleznete skladovací systém vhodný pro vaši potřebu, obraťte se přímo na nás a my vám pomůžeme najít vhodné řešení.",
			)}
		</p>
	</div>

	{#if selectedCategory === undefined || isCaseStudyCategory(selectedCategory) || filterOptions.length === 0}
		<p class="text-primary mb-4 mt-8 text-center text-xs font-extrabold md:hidden">Filtr referencí</p>
		<div
			class="flex flex-wrap justify-center gap-1 gap-y-2 px-4 sm:grid-cols-[repeat(2,17rem)] md:mt-8 md:grid md:gap-x-0 md:px-8 lg:grid-cols-[repeat(3,17rem)] lg:px-[2.625rem] xl:grid-cols-[repeat(5,17rem)]"
		>
			{#if selectedCategory === undefined || isCaseStudyCategory(selectedCategory)}
				{#each categories as category, index}
					<ReferenceFilter category={CaseStudyCategory[category]} largeIndex={index + 1} bind:selected />
				{/each}
			{/if}
			{#if filterOptions.length > 0}
				<ShelfSystemFilter {filterOptions} {selectedFilterOption} {countOfAllShelfSystemReferences} />
			{/if}
		</div>
	{/if}

	<div
		id="reference"
		class="mt-16 flex flex-wrap justify-center gap-5 gap-x-5 gap-y-16 sm:px-4 md:scroll-mt-20 lg:px-6"
	>
		{#each references.slice(0, visibleReferences) as reference}
			<Reference {reference} />
		{/each}
	</div>

	{#if visibleReferences < references.length}
		<div class="flex w-full justify-center px-4">
			<Button variant="secondary" class="mt-10 w-full max-w-[27.5rem]" on:click={handleLoadMore}>
				{nbspify("Načíst další reference")}
			</Button>
		</div>
	{/if}
	<div class="mt-20 flex flex-col gap-20 lg:mt-32 lg:gap-32">
		<HelpSection />
		<Contact />
	</div>
</Layout>
